/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import ZingTouch from 'zingtouch';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatSidenav } from '@angular/material/sidenav';
import { MapToolbarComponent } from '../toolbar/map-toolbar.component';
import { MapLegendComponent } from '../legend/map-legend.component';
import { LoggerService } from '../../shared/services/logger.service';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ReloadMap } from '../state/active-map.actions';
import { AuthState } from '../../../core/security/state/authState.state';
import { isEqual } from 'lodash-es';
import { MapConfigState } from '../state/map-config.state';
import { BottomSheetEnabled } from '../../../types/BottomSheetEnabled';

@Component({
    selector: 'dash-gis-map-container',
    templateUrl: './map-container.component.html',
    styleUrls: [ './map-container.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapContainerComponent implements OnInit, OnDestroy {
    public initializedSubject = new BehaviorSubject<boolean>(false);
    public isFullWidth = true;
    @ViewChild('bottomSheetView', { read: ViewContainerRef })
    private bottomSheetContainerRef: ViewContainerRef;

    @ViewChild('sidenav', { read: MatSidenav })
    private sidenavRef: MatSidenav;

    private sidenavOpenSubject = new BehaviorSubject<boolean>(false);
    private disposeSubject = new Subject<void>();
    private dispose$: Observable<void>;

    constructor(
        private logger: LoggerService,
        private viewContainerRef: ViewContainerRef,
        private bottomSheet: MatBottomSheet,
        private elementRef: ElementRef,
        private media: MediaObserver,
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef
    ) {
        this.dispose$ = this.disposeSubject.asObservable();

        this.media
            .asObservable()
            .pipe(
                map((changes) => changes.findIndex((c) => c.matches && c.mqAlias === 'gt-md') !== -1),
                takeUntil(this.dispose$)
            )
            .subscribe((result) => {
                this.isFullWidth = result;
            });
    }

    ngOnInit() {
        this.initialize();
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    // allow re-initializing without reloading
    initialize() {
        this.initializedSubject.next(false);
        this.cdr.detectChanges();

        this.actions$.pipe(ofActionSuccessful(ReloadMap), takeUntil(this.dispose$)).subscribe(() => this.reset());

        this.initializedSubject.next(true);
    }

    reset() {
        this.disposeSubject.next();
        this.initialize();
    }

    // enable the legend DOM before opening and after closing the sidenav/bottomsheet
    openSidenav() {
        this.sidenavOpenSubject.next(true);
        this.sidenavRef.open().then(() => {});
    }

    sidenavClosing() {
        this.sidenavOpenSubject.next(false);
    }

    openActionMenu() {
        const sheetRef = this.bottomSheet.open(MapToolbarComponent, {
            hasBackdrop: true,
            viewContainerRef: this.bottomSheetContainerRef,
            data: { isBottomSheet: true }
        });

        this.openSheet(sheetRef);
    }

    openLegendMenu() {
        const sheetRef = this.bottomSheet.open(MapLegendComponent, {
            hasBackdrop: true,
            viewContainerRef: this.bottomSheetContainerRef,
            data: {
                isBottomSheet: true,
                legendVisible: true
            }
        });

        this.openSheet(sheetRef);
    }

    private openSheet<T extends BottomSheetEnabled>(sheetRef: MatBottomSheetRef<T>) {
        const swipeHandler = (e: any) => {
            if (e.length === 1) {
                const event = e[0];
                if (event.direction && event.direction >= 250 && event.direction <= 290) {
                    this.bottomSheet.dismiss();
                }
            }
        };

        const ztRegion = new ZingTouch.Region(document.body);
        const el = sheetRef.instance.elementRef.nativeElement;
        sheetRef.afterOpened().subscribe(() => {
            ztRegion.bindOnce(el, 'swipe', swipeHandler, false);
        });
        sheetRef.afterDismissed().subscribe(() => {
            ztRegion.unbind(el);
        });
    }

    get initialized$() {
        return this.initializedSubject.asObservable();
    }

    get sidenavOpen$() {
        return this.sidenavOpenSubject.asObservable();
    }
}
